import React, {useEffect, useRef, useState} from 'react';

import {Button, CircularProgress, Link, Paper, Popover, TablePagination, TextField, Tooltip} from '@mui/material';
import {Cancel, Check, Delete, Edit, People, Reply, Visibility, VisibilityOff} from '@mui/icons-material';
import {useSnackbar} from '../../util/SnackbarProvider';
import {useConfirmationDialog} from '../../util/ConfirmationDialogProvider';
import authService from '../../services/AuthService';
import {limit} from '../../util/StringUtils';
import taskChatMessageService from "../../services/TaskChatMessageService";

const TaskChatMessages = () => {

    const urlParts = window.location.pathname.split('/')
    const taskId = urlParts[3]

    const [page, setPage] = useState(0);
    const [chatMessages, setChatMessages] = useState([]);
    const [newChatMessage, setNewChatMessage] = useState(localStorage.getItem("task-chatMessage-" + taskId) || "");
    const [replyToChatMessage, setReplyToChatMessage] = useState();
    const [rowsPerPage, setRowsPerPage] = useState(localStorage.getItem("task-chatMessages-rows-per-page") || 25);
    const [totalCount, setTotalCount] = useState(0);
    const [waitingForServer, setWaitingForServer] = useState(false)
    const {sendAlertMessage} = useSnackbar()
    const {openConfirmationDialog} = useConfirmationDialog()
    const loggedInUser = authService.getCurrentUser()
    const chatMessageDiv = useRef(null)
    const [popoverData, setPopoverData] = useState();

    useEffect(() => {
        retrieveChatMessages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    function buildQueryParams() {
        return {
            page: page,
            size: rowsPerPage,
        }
    }

    function retrieveChatMessages() {
        taskChatMessageService.findAllByTask(taskId, buildQueryParams())
            .then(response => {
                setTotalCount(response.totalElements)
                setChatMessages(response.content)
            })
            .catch((error) => sendAlertMessage(error?.message || "Kommentek lekérése sikertelen", "error"))
    }

    function sendChatMessage() {
        setWaitingForServer(true)
        taskChatMessageService.create(taskId, {message: newChatMessage, replyToChatMessageId: replyToChatMessage?.id})
            .then(() => {
                retrieveChatMessages()
                setNewChatMessage("")
                setReplyToChatMessage(undefined)
                localStorage.removeItem("task-chatMessage-" + taskId)
            })
            .catch((error) => sendAlertMessage(error.message, 'error'))
            .finally(() => setWaitingForServer(false))
    }

    function reactToChatMessage(chatMessageId, reactionType) {
        taskChatMessageService.react(chatMessageId, {reactionType: reactionType})
            .then(() => {
                retrieveChatMessages()
                setPopoverData(null)
            })
            .catch(error => sendAlertMessage(error?.message || "Reakció mentése sikertelen", 'error'))
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleSeenClick = (event, chatMessage) => {
        if (chatMessage.reactions?.find(r => r.userId === loggedInUser.userId)?.reactionType == null)
            setPopoverData({anchor: event.currentTarget, chatMessage: chatMessage})
        else reactToChatMessage(chatMessage.id, null)
    };

    const handleSeenClose = () => {
        setPopoverData(null);
    };

    const changeNewChatMessage = (event) => {
        const message = event.target.value
        localStorage.setItem("task-chat-message-" + taskId, message)
        setNewChatMessage(message);
    };

    const handleChangeRowsPerPage = (event) => {
        const rowsPerPage = event.target.value
        setRowsPerPage(event.target.value);
        localStorage.setItem("task-chat-messages-rows-per-page", rowsPerPage)
        setPage(0);
    };

    const handleEdit = (data) => {
        setChatMessages((prevChatMessages) =>
            prevChatMessages.map((chatMessage) =>
                chatMessage.id === data.id
                    ? {...chatMessage, currentlyEditing: true}
                    : {...chatMessage, currentlyEditing: false}
            )
        );
    };

    const handleChangeEditedChatMessage = (event, data) => {
        const newMessage = event.target.value;
        setChatMessages((prevChatMessages) =>
            prevChatMessages.map((chatMessage) =>
                chatMessage.id === data.id
                    ? {...chatMessage, message: newMessage}
                    : chatMessage
            )
        );
    };

    const saveEditedChatMessage = (data) => {
        taskChatMessageService.update(data.id, {message: data.message})
            .then(() => {
                sendAlertMessage('Komment sikeresen módosítva!')
                retrieveChatMessages()
            })
            .catch(error => sendAlertMessage(error, 'error'))
    };

    const deleteChatMessage = (data) => {
        taskChatMessageService.delete(data.id)
            .then(() => {
                sendAlertMessage('Komment sikeresen törölve!')
                retrieveChatMessages()
            })
            .catch(error => sendAlertMessage(error, 'error'))
    };

    const getWhoHasSeenList = (chatMessage) => {
        const result = chatMessage.reactions?.filter(r => r.reactionType !== null)
        if (result?.length === 0) return "Senki sem látta még"
        else if (result.length === 1) return result[0].username + ' látta (' + result[0].updatedAt + ")"
        else return (
                <>
                    {result.map((r, index) => (
                        <React.Fragment key={index}>
                            {r.username + " (" + r.updatedAt + ")"}
                            {index === result.length - 1 ? " látták" : <br/>}
                        </React.Fragment>
                    ))}
                </>
            );
    }

    const open = Boolean(popoverData?.anchor)

    const getBorderColor = (chatMessage) => {
        if (chatMessage.userId === loggedInUser.userId) return
        const user = chatMessage.reactions?.find(r => r.userId === loggedInUser.userId)
        switch (user?.reactionType) {
            case undefined:
                return "3px solid gold"
            case null:
                return "3px solid gold"
            case 'FURTHER_ACTION_REQUIRED':
                return "3px solid orange"
            case 'ACKNOWLEDGED':
                return ""
            default:
                console.log("Invalid user reaction type")
        }
    }

    return (
        <>
            <Paper sx={{marginTop: "15px", borderRadius: "5px", overflowAnchor: 'none'}}>
                {replyToChatMessage &&
                    <div style={{
                        fontSize: "12px",
                        fontStyle: "italic",
                        marginBottom: "5px",
                        backgroundColor: "lightgray",
                        borderRadius: "5px 5px 0px 0px",
                        padding: "10px 0px 10px 10px",
                        textAlign: "left"
                    }}>
                        <Reply style={{transform: 'rotate(180deg)', color: "gray", float: "left", marginTop: "5px"}}/>
                        <span style={{marginLeft: "10px"}}>
                            {limit(replyToChatMessage.message, 120)}
                        </span>
                        <Tooltip title='Mégse'>
                            <Button
                                color="error"
                                variant="text"
                                sx={{minWidth: "0px", padding: "5px", margin: "0px 0px 0px 10px"}}
                                onClick={() => setReplyToChatMessage(undefined)}
                            >
                                <Cancel/>
                            </Button>
                        </Tooltip>
                    </div>}
                <div style={{display: "flex", alignItems: "center"}}>
                    <div style={{flexGrow: 1, margin: "0px 10px 10px"}} ref={chatMessageDiv}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            multiline
                            label="Új Komment"
                            autoComplete="off"
                            onChange={changeNewChatMessage}
                            value={newChatMessage}
                        />
                    </div>
                    <div style={{textAlign: "right", margin: "0px 10px 0px 0px"}}>
                        {waitingForServer ? <CircularProgress/> :
                            <Button
                                component="label"
                                variant="contained"
                                color="primary"
                                style={{marginBottom: "10px"}}
                                onClick={() => sendChatMessage()}
                            >
                                Küldés
                            </Button>
                        }
                    </div>
                </div>
            </Paper>
            {chatMessages && chatMessages.map((chatMessage, index) =>
                <Paper key={index} sx={{margin: "15px 0px", borderRadius: "10px", border: getBorderColor(chatMessage)}}>
                    <div id="asd" style={{
                        backgroundColor: "lightgray",
                        color: "black",
                        padding: "10px",
                        borderRadius: "5px 5px 0px 0px",
                        textAlign: "left"
                    }}>
                        {chatMessage.replyToMessage &&
                            <div style={{fontSize: "12px", fontStyle: "italic", marginBottom: "5px"}}>
                                <Reply style={{
                                    transform: 'rotate(180deg)',
                                    color: "gray",
                                    float: "left",
                                    marginTop: "-3px"
                                }}/>
                                <span style={{marginLeft: "10px", textAlign: "left"}}>
                                    <Tooltip title={chatMessage.replyToMessage} placement='top'
                                             slotProps={{tooltip: {sx: {minWidth: '30vw'}}}}>
                                        {limit(chatMessage.replyToMessage, 120)}
                                    </Tooltip>
                                </span>
                            </div>}
                        {chatMessage.username}
                        <span style={{float: "right"}}>
                            <Tooltip title={getWhoHasSeenList(chatMessage)} placement='top'
                                     slotProps={{tooltip: {sx: {minWidth: '400px'}}}}>
                                <span>
                                    {chatMessage.updatedAt}
                                    <People sx={{marginLeft: "10px", float: "right"}}/>
                                </span>
                            </Tooltip>
                        </span>
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px",
                        whiteSpace: "pre-line"
                    }}>
                        {chatMessage.currentlyEditing ?
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                multiline
                                autoComplete="off"
                                onChange={(event) => handleChangeEditedChatMessage(event, chatMessage)}
                                value={chatMessage.message}
                            />
                            :
                            <span>{chatMessage.message}</span>
                        }
                        {chatMessage.currentlyEditing ?
                            <>
                                <Button
                                    color="success"
                                    variant="contained"
                                    sx={{minWidth: "0px", padding: "5px", marginLeft: "10px"}}
                                    onClick={() => saveEditedChatMessage(chatMessage)}
                                >
                                    <Check/>
                                </Button>
                                <Tooltip title='Mégse'>
                                    <Button
                                        color="error"
                                        variant="contained"
                                        sx={{minWidth: "0px", padding: "5px", marginLeft: "10px"}}
                                        onClick={() => handleEdit({})}
                                    >

                                        <Cancel/>
                                    </Button>
                                </Tooltip>
                            </>
                            :
                            <>
                                {chatMessage.userId === loggedInUser.userId ?
                                    <span style={{minWidth: "100px", textAlign: "right"}}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            sx={{minWidth: "0px", padding: "5px", marginLeft: "10px"}}
                                            onClick={() => handleEdit(chatMessage)}
                                        >
                                            <Edit/>
                                        </Button>
                                        <Button
                                            color="error"
                                            variant="contained"
                                            sx={{minWidth: "0px", padding: "5px", marginLeft: "10px"}}
                                            onClick={() => openConfirmationDialog(
                                                'Biztosan törölni szeretnéd az alábbi kommentet?',
                                                <>{limit(chatMessage.message, 50)}</>,
                                                () => deleteChatMessage(chatMessage)
                                            )}
                                        >
                                            <Delete/>
                                        </Button>
                                    </span>
                                    :
                                    <span style={{minWidth: "100px", textAlign: "right"}}>
                                        <Tooltip title="Válasz írása">
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                sx={{minWidth: "33px", padding: "5px", marginLeft: "10px"}}
                                                onClick={() => {
                                                    setReplyToChatMessage(chatMessage)
                                                    chatMessageDiv.current.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'center'
                                                    })
                                                }}
                                            >
                                                <Reply/>
                                            </Button>
                                        </Tooltip>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            sx={{minWidth: "33px", padding: "5px", marginLeft: "10px"}}
                                            onClick={(e) => handleSeenClick(e, chatMessage)}
                                        >
                                            {chatMessage.reactions?.find(r => r.userId === loggedInUser.userId)?.reactionType == null ?
                                                <Tooltip title="Láttam">
                                                    <Visibility/>
                                                </Tooltip>
                                                :
                                                <Tooltip title="Mégse láttam">
                                                    <VisibilityOff/>
                                                </Tooltip>}
                                        </Button>
                                    </span>
                                }
                            </>
                        }


                    </div>
                </Paper>)
            }
            <Popover
                open={open}
                anchorEl={popoverData?.anchor}
                onClose={handleSeenClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{minWidth: "500px"}}
            >
                <div style={{padding: "10px"}}><Link onClick={() => {
                    reactToChatMessage(popoverData.chatMessage.id, 'ACKNOWLEDGED')
                }} sx={{textDecoration: "none", cursor: "pointer", width: "100%", display: "block"}}>Rendben</Link>
                </div>
                <div style={{padding: "10px"}}><Link onClick={() => {
                    reactToChatMessage(popoverData.chatMessage.id, 'FURTHER_ACTION_REQUIRED')
                }} sx={{textDecoration: "none", cursor: "pointer", width: "100%", display: "block"}}>További intézkedés
                    szükséges</Link></div>
            </Popover>
            <Paper sx={{margin: "15px 0px", borderRadius: "5px"}}>
                <TablePagination
                    rowsPerPageOptions={[3, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={parseInt(rowsPerPage)}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>
        </>
    )

}

export default TaskChatMessages;